import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { FaArrowLeft, FaArrowRight, FaCar, FaCarSide, FaSearch, FaBell, FaUsers, FaClipboardList, FaCheckCircle, FaChartPie, FaBuilding, FaUser, FaTimes, FaTools, FaCalendarCheck, FaBoxOpen, FaCog, FaPoll, FaFolderOpen, FaFileCsv, FaEnvelope, FaShieldAlt, FaWallet, FaFileExcel } from 'react-icons/fa';
import VehicleList from './VehicleList';
import SearchVehicle from './SearchVehicle';
import NoticeBoard from '../notice/NoticeBoard';
import GuestVehicles from './GuestVehicleDashboard';
import AuditLogViewer from './AuditLogViewer';
import UserManagementHub from './UserManagementHub';
import UserAnalytics from './UserAnalytics';
import CommunityManagement from './CommunityManagement';
import MemberList from './MemberList';
import SearchMembers from './SearchMembers';
import DomesticHelp from '../domestic/DomesticHelp'; // Added import
import Helpdesk from '../engagement/Helpdesk';
import Amenities from '../engagement/Amenities';
import Parcels from '../engagement/Parcels';
import PollsEvents from '../engagement/PollsEvents';
import Documents from '../engagement/Documents';
import Reports from '../engagement/Reports';
import PlatformSettings from '../engagement/PlatformSettings';
import AccessControlManagement from '../engagement/AccessControlManagement';
import NotificationDeliveryLog from '../engagement/NotificationDeliveryLog';
import VehicleDocumentVerification from '../engagement/VehicleDocumentVerification';
import AppAccessAnalytics from './AppAccessAnalytics';
import CommunityFinance from '../finance/CommunityFinance';
import CommunityOnboarding from './CommunityOnboarding';
import DemoDataManagement from './DemoDataManagement';
import useApi from '../../hooks/useApi';
import { toast } from 'react-toastify';
import CommunityIdentity, { CommunityAvatar } from '../common/CommunityIdentity';

const localDateKey = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

function CommonDashboard() {
  const { session } = useContext(AuthContext);
  const { roles, communityId, communityName } = session || {
    roles: ['GUEST'],
    communityId: null,
    communityName: '',
  };
  const isSuperAdmin = roles.includes('SUPER_ADMIN');
  const { get } = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSection = searchParams.get('section');
  const [currentSection, setCurrentSection] = useState(requestedSection || null);
  const [vehicles, setVehicles] = useState([]);
  const [members, setMembers] = useState([]);
  const [notices, setNotices] = useState([]);
  const [guestVehicles, setGuestVehicles] = useState([]);
  const [amenityBookings, setAmenityBookings] = useState([]);
  const [engagementCounts, setEngagementCounts] = useState({ polls: 0, events: 0 });
  const [isServiceDrawerOpen, setIsServiceDrawerOpen] = useState(false);
  const [serviceQuery, setServiceQuery] = useState('');
  const [noticeIndex, setNoticeIndex] = useState(0);
  const [isNoticePaused, setIsNoticePaused] = useState(false);
  const [amenityIndex, setAmenityIndex] = useState(0);
  const [isAmenityPaused, setIsAmenityPaused] = useState(false);
  const [visitorPage, setVisitorPage] = useState(0);
  const [isVisitorPaused, setIsVisitorPaused] = useState(false);
  const [featureSettings, setFeatureSettings] = useState(null);
  const [entitledServices, setEntitledServices] = useState(null);
  const [accessProfile, setAccessProfile] = useState(null);

  const getSearchVehicleIcon = () => (
    <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
      <FaCarSide size={30} color="red" />
      <FaSearch size={20} />
    </span>
  );

  const getSearchMemberIcon = () => (
    <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
      <FaUser size={30} color="purple" />
      <FaSearch size={20} />
    </span>
  );

  useEffect(() => {
    if (roles.includes('USER')) {
      fetchVehicles();
      fetchMembers();
    }
  }, [roles]);

  useEffect(() => {
    let cancelled = false;
    const fetchOverview = async () => {
      const params = !isSuperAdmin && communityId ? { communityId } : {};
      const [guestResult, noticeResult, bookingResult, pollResult, eventResult] = await Promise.allSettled([
        get('/api/guest-vehicles', { params }),
        get('/api/notices', { params }),
        isSuperAdmin && !communityId ? Promise.resolve([]) : get('/api/amenities/bookings/upcoming', { params }),
        isSuperAdmin && !communityId ? Promise.resolve([]) : get('/api/polls'),
        isSuperAdmin && !communityId ? Promise.resolve([]) : get('/api/events'),
      ]);
      if (cancelled) return;
      if (guestResult.status === 'fulfilled') setGuestVehicles(guestResult.value || []);
      if (noticeResult.status === 'fulfilled') setNotices(noticeResult.value || []);
      if (bookingResult.status === 'fulfilled') setAmenityBookings(bookingResult.value || []);
      setEngagementCounts({
        polls: pollResult.status === 'fulfilled' ? (pollResult.value || []).filter(item => item.status === 'OPEN').length : 0,
        events: eventResult.status === 'fulfilled' ? (eventResult.value || []).filter(item => item.status === 'PUBLISHED').length : 0,
      });
    };
    fetchOverview();
    const refreshTimer = window.setInterval(fetchOverview, 60000);
    return () => { cancelled = true; window.clearInterval(refreshTimer); };
  }, [communityId, isSuperAdmin, get]);

  useEffect(() => {
    if (isSuperAdmin) return;
    get('/api/community-service-policies').then(items => setFeatureSettings(Object.fromEntries((items || []).map(item => [item.serviceCode, item.platformAllowed && item.communityEnabled])))).catch(() => setFeatureSettings(null));
  }, [isSuperAdmin, communityId, get]);

  useEffect(() => {
    if (!communityId) {
      setEntitledServices([]);
      return;
    }
    get('/api/subscriptions/effective', { params: { communityId } })
      .then(items => setEntitledServices(Array.from(items || [])))
      .catch(() => setEntitledServices([]));
  }, [communityId, get]);

  useEffect(() => { get('/api/access-control/me').then(setAccessProfile).catch(() => setAccessProfile(null)); }, [get]);

  useEffect(() => {
    if (!isServiceDrawerOpen) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') setIsServiceDrawerOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    document.body.classList.add('service-drawer-open');
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.body.classList.remove('service-drawer-open');
    };
  }, [isServiceDrawerOpen]);

  const fetchVehicles = async () => {
    try {
      const response = await get('/api/vehicles');
      setVehicles(response || []);
    } catch (err) {
      console.error('CommonDashboard - Fetch vehicles error:', err);
      toast.error('Failed to load vehicles.');
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await get('/api/members');
      setMembers(response || []);
    } catch (err) {
      console.error('CommonDashboard - Fetch members error:', err);
      toast.error('Failed to load members.');
    }
  };

  const roleTabs = {
    USER: [
      { icon: <FaCar size={30} color="#28a745" />, title: 'My Vehicles', tooltip: 'View and manage your vehicles', section: 'vehicles' },
      { icon: getSearchVehicleIcon(), title: 'Search Vehicle', tooltip: 'Find registered vehicles in your community', section: 'searchVehicle' },
      { icon: <FaUser size={30} color="#6f42c1" />, title: 'My Members', tooltip: 'View and manage your family members', section: 'members' },
      { icon: getSearchMemberIcon(), title: 'Search Members', tooltip: 'Search for members in the system', section: 'searchMembers' },
      { icon: <FaBell size={30} className="text-primary" />, title: 'Notice Board', tooltip: 'View community notices', section: 'notice' },
      { icon: <FaCar size={30} className="text-primary" />, title: 'Visitors', tooltip: 'Create and monitor visitor entries', section: 'guestVehicles' },
      { icon: <FaUser size={30} color="#ff6f61" />, title: 'Domestic Help', tooltip: 'View and manage domestic helpers', section: 'domesticHelp' }, // Added tab
      { icon: <FaTools />, title: 'Helpdesk', tooltip: 'Raise and track community issues', section: 'helpdesk' },
      { icon: <FaCalendarCheck />, title: 'Amenities', tooltip: 'Book community facilities', section: 'amenities' },
      { icon: <FaBoxOpen />, title: 'Parcels', tooltip: 'Track parcels received at the gate', section: 'parcels' },
      { icon: <FaPoll />, title: 'Polls & Events', tooltip: 'Vote and join community activities', section: 'pollsEvents' },
      { icon: <FaFolderOpen />, title: 'Documents', tooltip: 'Manage your resident documents', section: 'documents' },
      { icon: <FaWallet />, title: 'Community Finance', tooltip: 'View dues, collections, payments and receipts', section: 'finance' },
    ],
    ADMIN: [
      { icon: <FaUsers size={30} className="text-primary" />, title: 'Manage Users', tooltip: 'View and manage community members', section: 'manageUsers' },
      { icon: <FaChartPie size={30} className="text-primary" />, title: 'Community Analytics', tooltip: 'Review community accounts, residents and configuration health', section: 'analytics' },
      { icon: <FaClipboardList size={30} className="text-primary" />, title: 'Audit Trail', tooltip: 'Review system logs and activities', section: 'audit' },
      { icon: <FaCar size={30} className="text-primary" />, title: 'Visitors', tooltip: 'Manage visitor entries and approvals', section: 'guestVehicles' },
      { icon: <FaBell size={30} className="text-primary" />, title: 'Notice Board', tooltip: 'View and manage community notices', section: 'notice' },
      { icon: getSearchVehicleIcon(), title: 'Search Vehicle', tooltip: 'Search for vehicles in the system', section: 'searchVehicle' },
      { icon: getSearchMemberIcon(), title: 'Search Members', tooltip: 'Search for members in the system', section: 'searchMembers' },
      { icon: <FaUser size={30} color="#ff6f61" />, title: 'Domestic Help', tooltip: 'View and manage domestic helpers', section: 'domesticHelp' }, // Added tab
      { icon: <FaTools />, title: 'Helpdesk', tooltip: 'Manage resident complaints', section: 'helpdesk' },
      { icon: <FaCalendarCheck />, title: 'Amenities', tooltip: 'Configure facilities and bookings', section: 'amenities' },
      { icon: <FaBoxOpen />, title: 'Parcels', tooltip: 'Monitor parcel custody', section: 'parcels' },
      { icon: <FaPoll />, title: 'Polls & Events', tooltip: 'Publish polls and community events', section: 'pollsEvents' },
      { icon: <FaFolderOpen />, title: 'Documents', tooltip: 'Manage resident documents', section: 'documents' },
      { icon: <FaFileCsv />, title: 'Reports', tooltip: 'Export visitor and complaint reports', section: 'reports' },
      { icon: <FaFolderOpen />, title: 'Vehicle Documents', tooltip: 'Verify resident RC submissions', section: 'vehicleDocuments' },
      { icon: <FaShieldAlt />, title: 'Access Control', tooltip: 'Manage roles, permissions and workflow rights', section: 'accessControl' },
      { icon: <FaEnvelope />, title: 'Notification Log', tooltip: 'Track outbound notification delivery', section: 'notificationLog' },
      { icon: <FaWallet />, title: 'Community Finance', tooltip: 'Manage collections, payments, income and expenses', section: 'finance' },
      { icon: <FaBuilding />, title: 'Homes & Residents', tooltip: 'Configure homes and verify resident assignments', section: 'residentConfiguration' },
    ],
    SECURITY: [
      { icon: <FaCar size={30} className="text-primary" />, title: 'Visitors', tooltip: 'Manage visitor gate activity', section: 'guestVehicles' },
      { icon: getSearchVehicleIcon(), title: 'Search Vehicle', tooltip: 'Search for vehicles in the system', section: 'searchVehicle' },
      { icon: getSearchMemberIcon(), title: 'Search Members', tooltip: 'Search for members in the system', section: 'searchMembers' },
      { icon: <FaBell size={30} className="text-primary" />, title: 'Notice Board', tooltip: 'View community notices', section: 'notice' },
      { icon: <FaUser size={30} color="#ff6f61" />, title: 'Domestic Help', tooltip: 'View domestic helpers', section: 'domesticHelp' }, // Added tab
      { icon: <FaBoxOpen />, title: 'Parcels', tooltip: 'Receive and release resident parcels', section: 'parcels' },
    ],
    SUPER_ADMIN: [
      { icon: <FaUsers size={30} className="text-primary" />, title: 'Manage Users', tooltip: 'View and manage community members', section: 'manageUsers' },
      { icon: <FaChartPie size={30} color="#2196f3" />, title: 'Community Analytics', tooltip: 'Review accounts and resident configuration across communities', section: 'analytics' },
      { icon: <FaChartPie size={30} color="#16a085" />, title: 'App Analytics', tooltip: 'Review visits, locations and devices', section: 'appAnalytics' },
      { icon: <FaClipboardList size={30} className="text-primary" />, title: 'Audit Trail', tooltip: 'Review system logs', section: 'audit' },
      { icon: <FaCar size={30} className="text-primary" />, title: 'Visitors', tooltip: 'Monitor visitor entries', section: 'guestVehicles' },
      { icon: <FaBell size={30} className="text-primary" />, title: 'Notice Board', tooltip: 'Manage community notices', section: 'notice' },
      { icon: getSearchVehicleIcon(), title: 'Search Vehicle', tooltip: 'Search across all communities', section: 'searchVehicle' },
      { icon: getSearchMemberIcon(), title: 'Search Members', tooltip: 'Search members across communities', section: 'searchMembers' },
      { icon: <FaBuilding size={34} color="#3b5998" />, title: 'Manage Communities', tooltip: 'Manage communities', section: 'communities' },
      { icon: <FaFileExcel size={32} color="#217346" />, title: 'Demo Data', tooltip: 'Download, validate and import a demonstration workbook', section: 'demoData' },
      { icon: <FaUser size={35} color="#ff6f61" />, title: 'Domestic Help', tooltip: 'Manage all domestic helpers', section: 'domesticHelp' }, // Added tab
      { icon: <FaTools />, title: 'Helpdesk', tooltip: 'Manage community complaints', section: 'helpdesk' },
      { icon: <FaCalendarCheck />, title: 'Amenities', tooltip: 'Configure facilities and bookings', section: 'amenities' },
      { icon: <FaBoxOpen />, title: 'Parcels', tooltip: 'Monitor parcel custody', section: 'parcels' },
      { icon: <FaPoll />, title: 'Polls & Events', tooltip: 'Publish polls and community events', section: 'pollsEvents' },
      { icon: <FaFolderOpen />, title: 'Documents', tooltip: 'Manage resident documents', section: 'documents' },
      { icon: <FaFileCsv />, title: 'Reports', tooltip: 'Export community reports', section: 'reports' },
      { icon: <FaFolderOpen />, title: 'Vehicle Documents', tooltip: 'Verify resident RC submissions', section: 'vehicleDocuments' },
      { icon: <FaEnvelope />, title: 'Contact Settings', tooltip: 'Configure the public support mailbox', section: 'platformSettings' },
      { icon: <FaShieldAlt />, title: 'Access Control', tooltip: 'Manage platform and community access roles', section: 'accessControl' },
      { icon: <FaEnvelope />, title: 'Notification Log', tooltip: 'Track outbound notification delivery', section: 'notificationLog' },
    ],
    GUEST: [],
  };

  const featureForSection = { vehicles:'VEHICLES',searchVehicle:'VEHICLES',members:'MEMBERS',searchMembers:'MEMBERS',notice:'NOTICES',guestVehicles:'VISITORS',manageUsers:'USERS',residentConfiguration:'USERS',analytics:'USERS',audit:'AUDIT',domesticHelp:'DOMESTIC_HELP',helpdesk:'HELPDESK',amenities:'AMENITIES',parcels:'PARCELS',pollsEvents:'POLLS_EVENTS',documents:'DOCUMENTS',reports:'REPORTS',notificationLog:'NOTIFICATIONS',finance:'FINANCE' };
  const accessServiceForSection = { vehicles:'VEHICLES',searchVehicle:'VEHICLES',vehicleDocuments:'VEHICLES',members:'MEMBERS',searchMembers:'MEMBERS',notice:'NOTICES',guestVehicles:'VISITORS',manageUsers:'USERS',residentConfiguration:'USERS',approveUsers:'USERS',analytics:'USERS',appAnalytics:'PLATFORM_SETTINGS',audit:'AUDIT',communities:'COMMUNITIES',domesticHelp:'DOMESTIC_HELP',helpdesk:'HELPDESK',amenities:'AMENITIES',parcels:'PARCELS',settings:'SETTINGS',pollsEvents:'POLLS_EVENTS',documents:'DOCUMENTS',reports:'REPORTS',platformSettings:'PLATFORM_SETTINGS',accessControl:'ACCESS_CONTROL',notificationLog:'NOTIFICATIONS',finance:'FINANCE' };
  const requiresCommunityContext = new Set(['helpdesk', 'amenities', 'parcels', 'settings', 'pollsEvents', 'documents', 'vehicleDocuments']);
  const availableTabs = roles
    .flatMap(role => roleTabs[role] || [])
    .reduce((unique, tab) => {
      if (!unique.some(t => t.title === tab.title)) {
        unique.push(tab);
      }
      return unique;
    }, [])
    .filter(tab => !(isSuperAdmin && !communityId && requiresCommunityContext.has(tab.section)))
    .filter(tab => tab.section !== 'finance' || (Boolean(communityId) && entitledServices?.includes('FINANCE')))
    .filter(tab => !featureSettings || !featureForSection[tab.section] || featureSettings[featureForSection[tab.section]] !== false)
    .filter(tab => isSuperAdmin || !accessProfile?.customRolesAssigned || accessProfile.permissions?.includes(`${accessServiceForSection[tab.section]}:VIEW`) || accessProfile.permissions?.includes(`${accessServiceForSection[tab.section]}:MANAGE`) || accessProfile.permissions?.includes('*:MANAGE'));

  useEffect(() => {
    if (availableTabs.length > 0) {
      const requestedTab = availableTabs.find(tab => tab.section === requestedSection);
      const nextSection = requestedTab?.section || null;
      if (nextSection !== currentSection) setCurrentSection(nextSection);
    }
  }, [availableTabs, requestedSection, currentSection]);

  const handleSectionChange = (section) => {
    setCurrentSection(section);
    setSearchParams({ section });
  };

  const openService = section => {
    setIsServiceDrawerOpen(false);
    setServiceQuery('');
    handleSectionChange(section);
  };

  const showAllServices = () => {
    setCurrentSection(null);
    setSearchParams({});
  };

  const openSummary = item => {
    if (item.section) {
      openService(item.section);
      return;
    }
    setIsServiceDrawerOpen(true);
  };

  const activeTab = currentSection
    ? availableTabs.find(tab => tab.section === currentSection) || null
    : null;
  const displayRole = roles.find(role => role !== 'GUEST') || 'Resident';
  const filteredServices = availableTabs.filter(tab =>
    `${tab.title} ${tab.tooltip}`.toLowerCase().includes(serviceQuery.trim().toLowerCase())
  );
  const sortedNotices = useMemo(() => {
    const today = localDateKey(new Date());
    return notices
      .filter(notice => notice.status !== 'EXPIRED' && (!notice.endDate || notice.endDate.slice(0, 10) >= today))
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [notices]);
  const currentNotice = sortedNotices[noticeIndex] || null;
  const activeAmenityBookings = useMemo(() => amenityBookings
    .filter(booking => booking.status === 'CONFIRMED' && new Date(booking.endTime) >= new Date())
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime)), [amenityBookings]);
  const currentAmenityBooking = activeAmenityBookings[amenityIndex] || null;
  const sortedActiveVisitors = useMemo(() => [...guestVehicles]
    .sort((a, b) => new Date(b.actualEntryTime || b.entryTime || 0) - new Date(a.actualEntryTime || a.entryTime || 0)), [guestVehicles]);
  const visitorPageCount = Math.max(1, Math.ceil(sortedActiveVisitors.length / 3));
  const visibleVisitors = sortedActiveVisitors.slice(visitorPage * 3, visitorPage * 3 + 3);

  useEffect(() => {
    setNoticeIndex(0);
  }, [sortedNotices.length]);

  useEffect(() => {
    setAmenityIndex(0);
  }, [activeAmenityBookings.length]);

  useEffect(() => {
    setVisitorPage(0);
  }, [sortedActiveVisitors.length]);

  useEffect(() => {
    if (sortedNotices.length < 2 || isNoticePaused) return undefined;
    const rotation = window.setInterval(() => {
      setNoticeIndex(current => (current + 1) % sortedNotices.length);
    }, 6500);
    return () => window.clearInterval(rotation);
  }, [sortedNotices.length, isNoticePaused]);

  useEffect(() => {
    if (activeAmenityBookings.length < 2 || isAmenityPaused) return undefined;
    const rotation = window.setInterval(() => {
      setAmenityIndex(current => (current + 1) % activeAmenityBookings.length);
    }, 6500);
    return () => window.clearInterval(rotation);
  }, [activeAmenityBookings.length, isAmenityPaused]);

  useEffect(() => {
    if (visitorPageCount < 2 || isVisitorPaused) return undefined;
    const rotation = window.setInterval(() => {
      setVisitorPage(current => (current + 1) % visitorPageCount);
    }, 6500);
    return () => window.clearInterval(rotation);
  }, [visitorPageCount, isVisitorPaused]);

  const changeNotice = direction => {
    setNoticeIndex(current => (current + direction + sortedNotices.length) % sortedNotices.length);
  };

  const changeAmenity = direction => {
    setAmenityIndex(current => (current + direction + activeAmenityBookings.length) % activeAmenityBookings.length);
  };

  const getNoticeCreatorFirstName = notice => {
    const creator = notice?.createdByName?.trim() || notice?.createdByUsername?.trim();
    return creator ? creator.split(/\s+/)[0] : 'Creator unavailable';
  };

  const summaryItems = [
    { value: sortedActiveVisitors.length, label: 'Active visitors', section: 'guestVehicles' },
    { value: sortedNotices.length, label: 'Current notices', section: 'notice' },
    { value: engagementCounts.polls, label: 'Active polls', section: 'pollsEvents' },
    { value: engagementCounts.events, label: 'Active events', section: 'pollsEvents' },
    roles.includes('USER')
      ? { value: vehicles.length, label: 'Your vehicles', section: 'vehicles' }
      : { value: availableTabs.length, label: 'Available tools' },
    roles.includes('USER')
      ? { value: members.length, label: 'Family members', section: 'members' }
      : { value: displayRole.replace('_', ' '), label: 'Access level' },
  ];

  const renderSection = () => {
    switch (currentSection) {
      case 'vehicles':
        return (
          <div className="row">
            <div className="col-12 mb-4">
              <VehicleList vehicles={vehicles} setVehicles={setVehicles} />
            </div>
          </div>
        );
      case 'members':
        return (
          <div className="row">
            <div className="col-md-12 mb-4">
              <MemberList members={members} setMembers={setMembers} />
            </div>
          </div>
        );
      case 'searchVehicle':
        return <SearchVehicle isSuperAdmin={isSuperAdmin} />;
      case 'searchMembers':
        return <SearchMembers isSuperAdmin={isSuperAdmin} />;
      case 'notice':
        return <NoticeBoard />;
      case 'guestVehicles':
        return <GuestVehicles />;
      case 'manageUsers':
        return <UserManagementHub isSuperAdmin={isSuperAdmin} />;
      case 'approveUsers':
        return <ApproveUsers />;
      case 'audit':
        return <AuditLogViewer />;
      case 'analytics':
        return <UserAnalytics />;
      case 'appAnalytics':
        return <AppAccessAnalytics />;
      case 'communities':
        return <CommunityManagement />;
      case 'demoData':
        return <DemoDataManagement />;
      case 'residentConfiguration':
        return (
          <CommunityOnboarding
            communityId={communityId}
            community={session.community}
            communityName={communityName}
            homesOnly
          />
        );
      case 'domesticHelp':
        return <DomesticHelp />; // Added section
      case 'helpdesk':
        return <Helpdesk />;
      case 'amenities':
        return <Amenities />;
      case 'parcels':
        return <Parcels />;
      case 'pollsEvents':
        return <PollsEvents />;
      case 'documents':
        return <Documents />;
      case 'reports':
        return <Reports />;
      case 'platformSettings':
        return <PlatformSettings />;
      case 'accessControl':
        return <AccessControlManagement />;
      case 'notificationLog':
        return <NotificationDeliveryLog />;
      case 'vehicleDocuments':
        return <VehicleDocumentVerification />;
      case 'finance':
        return <CommunityFinance />;
      default:
        return <div>No section selected.</div>;
    }
  };

  return (
    <main className="community-dashboard animate__animated animate__fadeIn">
      {!activeTab && (
      <div className="dashboard-welcome command-identity">
        <div>
          <CommunityIdentity
            community={session.community || { name: communityName, displayName: session.communityDisplayName }}
            size="sm"
            compact
          />
          <h1>Good to see you{session.name ? `, ${session.name.split(' ')[0]}` : ''}</h1>
          <div className="dashboard-welcome-meta">
            <span>{displayRole.replace('_', ' ').toLowerCase()}</span>
            <span>{availableTabs.length} services available</span>
          </div>
          <p>Everything you need for your home, visitors and community—in one place.</p>
        </div>
        <div className="dashboard-home-mark" aria-hidden="true">
          <CommunityAvatar
            community={session.community || { name: communityName, displayName: session.communityDisplayName }}
            size="lg"
          />
        </div>
      </div>
      )}
      {!activeTab ? (
        <section className="command-center" aria-label="Community overview">
          <div className="command-summary" aria-label="Today at a glance">
            {summaryItems.map(item => (
              <button key={item.label} type="button" className="command-summary-card" onClick={() => openSummary(item)} aria-label={`Open ${item.label}`}>
                <strong>{item.value}</strong><span>{item.label}</span><FaArrowRight aria-hidden="true" />
              </button>
            ))}
          </div>
          <div className="command-layout">
            <div className="command-main">
              <section className="command-card command-actions">
                <header><div><span>Services</span><h2>Everything you can access</h2></div><button type="button" className="all-services-button" onClick={() => setIsServiceDrawerOpen(true)}>Search services <FaSearch /></button></header>
                <div className="command-service-grid">
                  {availableTabs.map(tab => (
                    <button key={tab.section} type="button" onClick={() => openService(tab.section)}>
                      <span className="command-service-icon">{tab.icon}</span>
                      <span className="command-service-copy"><strong>{tab.title}</strong><small>{tab.tooltip}</small></span>
                      <FaArrowRight className="command-service-arrow" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              </section>
              <section className="command-card command-visitors">
                <header><div><span>Live activity</span><h2>Visitors in your community</h2></div><button type="button" onClick={() => openService('guestVehicles')}>View all</button></header>
                {sortedActiveVisitors.length > 0 ? (
                  <div onMouseEnter={() => setIsVisitorPaused(true)} onMouseLeave={() => setIsVisitorPaused(false)} onFocus={() => setIsVisitorPaused(true)} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setIsVisitorPaused(false); }}>
                  <div className="command-visitor-list" aria-live="polite">
                    {visibleVisitors.map(vehicle => (
                      <button type="button" key={vehicle.id} onClick={() => openService('guestVehicles')}>
                        <span className="visitor-initial">{(vehicle.guestName || 'G').charAt(0).toUpperCase()}</span>
                        <span><strong>{vehicle.guestName || 'Visitor'}</strong><small>{vehicle.visitorCategory || 'GUEST'} · {vehicle.vehicleNo || 'Vehicle not recorded'} · {vehicle.division ? `${vehicle.division}-` : ''}{vehicle.visitingHouseNo || 'Community visit'}</small></span>
                        <span className="visitor-status">{vehicle.status || 'Inside'}</span>
                      </button>
                    ))}
                  </div>
                  {visitorPageCount > 1 && <div className="notice-controls" aria-label="Visitor carousel controls"><button type="button" onClick={() => setVisitorPage(current => (current - 1 + visitorPageCount) % visitorPageCount)} aria-label="Previous visitors"><FaArrowLeft /></button><span className="carousel-count">{visitorPage + 1} / {visitorPageCount}</span><button type="button" onClick={() => setVisitorPage(current => (current + 1) % visitorPageCount)} aria-label="Next visitors"><FaArrowRight /></button></div>}
                  </div>
                ) : <div className="command-empty"><strong>No active visitors</strong><span>New visitor arrivals will appear here.</span></div>}
              </section>
            </div>
            <aside className="command-side">
              <section className="command-card command-notice">
                <header><div><span>Community updates</span><h2>Notice board</h2></div><button type="button" onClick={() => openService('notice')}>View all</button></header>
                {currentNotice ? (
                  <div className="notice-carousel" onMouseEnter={() => setIsNoticePaused(true)} onMouseLeave={() => setIsNoticePaused(false)} onFocus={() => setIsNoticePaused(true)} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setIsNoticePaused(false); }}>
                    <button type="button" className="latest-notice" onClick={() => openService('notice')} aria-live="polite">
                      <span>{currentNotice.status || 'COMMUNITY'}</span>
                      <strong>{currentNotice.title}</strong>
                      <p>{currentNotice.content}</p>
                      <small className="notice-byline"><span>Created by {getNoticeCreatorFirstName(currentNotice)}</span><span>{currentNotice.createdAt ? new Date(currentNotice.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Recently published'}</span></small>
                    </button>
                    {sortedNotices.length > 1 && (
                      <div className="notice-controls" aria-label="Notice carousel controls">
                        <button type="button" onClick={() => changeNotice(-1)} aria-label="Previous notice"><FaArrowLeft /></button>
                        <div className="notice-dots" aria-label={`Notice ${noticeIndex + 1} of ${sortedNotices.length}`}>
                          {sortedNotices.map((notice, index) => <button key={notice.id || index} type="button" className={index === noticeIndex ? 'is-active' : ''} onClick={() => setNoticeIndex(index)} aria-label={`Show notice ${index + 1}`} />)}
                        </div>
                        <button type="button" onClick={() => changeNotice(1)} aria-label="Next notice"><FaArrowRight /></button>
                      </div>
                    )}
                  </div>
                ) : <div className="command-empty"><strong>You are all caught up</strong><span>No active community notices.</span></div>}
              </section>
              <section className="command-card command-notice"><header><div><span>Community schedule</span><h2>Upcoming bookings</h2></div><button type="button" onClick={() => openService('amenities')}>Amenities</button></header>{currentAmenityBooking ? <div className="notice-carousel" onMouseEnter={() => setIsAmenityPaused(true)} onMouseLeave={() => setIsAmenityPaused(false)} onFocus={() => setIsAmenityPaused(true)} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setIsAmenityPaused(false); }}><button type="button" className="latest-notice upcoming-booking-card" onClick={() => openService('amenities')} aria-live="polite"><span>APPROVED</span><strong>{currentAmenityBooking.amenityName}</strong><p>{new Date(currentAmenityBooking.startTime).toLocaleString('en-IN')} – {new Date(currentAmenityBooking.endTime).toLocaleString('en-IN')}<br />{currentAmenityBooking.purpose || 'Community amenity reservation'} · {currentAmenityBooking.attendees} attendee{currentAmenityBooking.attendees === 1 ? '' : 's'}</p><div className="booking-audit"><small><span>Requested by</span><strong>{currentAmenityBooking.bookedByName}</strong><time>{new Date(currentAmenityBooking.createdAt).toLocaleString('en-IN')}</time></small><small><span>Approved by</span><strong>{currentAmenityBooking.decidedByName || 'Community admin'}</strong><time>{currentAmenityBooking.decidedAt ? new Date(currentAmenityBooking.decidedAt).toLocaleString('en-IN') : 'Approval recorded'}</time></small></div></button>{activeAmenityBookings.length > 1 && <div className="notice-controls" aria-label="Amenity booking carousel controls"><button type="button" onClick={() => changeAmenity(-1)} aria-label="Previous booking"><FaArrowLeft /></button>{activeAmenityBookings.length <= 8 ? <div className="notice-dots" aria-label={`Booking ${amenityIndex + 1} of ${activeAmenityBookings.length}`}>{activeAmenityBookings.map((booking,index) => <button key={booking.id || index} type="button" className={index === amenityIndex ? 'is-active' : ''} onClick={() => setAmenityIndex(index)} aria-label={`Show booking ${index + 1}`} />)}</div> : <span className="carousel-count">{amenityIndex + 1} / {activeAmenityBookings.length}</span>}<button type="button" onClick={() => changeAmenity(1)} aria-label="Next booking"><FaArrowRight /></button></div>}</div> : <div className="command-empty amenity-empty"><span><FaCalendarCheck /></span><strong>No upcoming bookings</strong><small>Approved community amenity bookings will appear here.</small><button type="button" onClick={() => openService('amenities')}>Explore amenities</button></div>}</section>
              <section className="command-card command-account">
                <span>Your access</span><strong>{displayRole.replace('_', ' ').toLowerCase()}</strong><p>{session.communityDisplayName || 'Your assigned community'}</p><button type="button" onClick={() => setIsServiceDrawerOpen(true)}>Browse {availableTabs.length} available services</button>
              </section>
            </aside>
          </div>
        </section>
      ) : (
        <section className="dashboard-content dashboard-focus" aria-live="polite">
          <header className="dashboard-content-header">
            <button type="button" className="service-back" onClick={showAllServices}><FaArrowLeft /><span>All services</span></button>
            <span className="dashboard-content-icon">{activeTab.icon}</span>
            <div className="dashboard-content-title">
              <span className="dashboard-content-kicker">Community service</span>
              <h2>{activeTab.title}</h2>
              <p>{activeTab.tooltip}</p>
            </div>
          </header>
          <div className="dashboard-content-body">{renderSection()}</div>
        </section>
      )}
      {isServiceDrawerOpen && (
        <div className="service-drawer-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) setIsServiceDrawerOpen(false); }}>
          <aside className="service-drawer" role="dialog" aria-modal="true" aria-labelledby="service-drawer-title">
            <header><div><span>Navigate</span><h2 id="service-drawer-title">All services</h2><p>Search or choose a community function.</p></div><button type="button" onClick={() => setIsServiceDrawerOpen(false)} aria-label="Close services"><FaTimes /></button></header>
            <label className="service-search"><FaSearch /><input autoFocus type="search" value={serviceQuery} onChange={event => setServiceQuery(event.target.value)} placeholder="Search services" /></label>
            <nav className="service-drawer-list" aria-label="All community services">
              {filteredServices.map(tab => <button type="button" key={tab.section} onClick={() => openService(tab.section)}><span><strong>{tab.title}</strong><small>{tab.tooltip}</small></span><FaArrowRight /></button>)}
              {filteredServices.length === 0 && <div className="service-no-results">No services match “{serviceQuery}”.</div>}
            </nav>
          </aside>
        </div>
      )}
    </main>
  );
}

export default CommonDashboard;
